import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';
import {
	TApiResponse,
	TMintHistory,
	TMintHistoryQueryParams,
	TMintHistoryModified,
} from '../types';
import {
	convertGCO2EToTCO2E,
	formatDate,
} from '@utils/functions';
import {
	getMintHistoryPercent,
	isAllStatusSuccess,
} from '@utils/mint-status';
import { NOT_APPLICABLE } from '@config/constants/common';
import { formatNumberWithSpaces } from '@utils/formats';

export const getMintHistories = (
	page: number,
	size: number,
	queryParams: Partial<TMintHistoryQueryParams>
): Promise<TApiResponse> => {
	return apiClient.get('/aco2_tokens', {
		params: {
			page: page,
			pageSize: size,
			populate: ['project'],
			query: queryParams,
			sort: '-createdAt',
			excludeFields: [
				'createdBy',
				'details',
				'entity',
				'createdAt',
				'updatedAt',
				'blockchain',
				'endDate',
				'transactionHash',
			],
		},
	});
};

export const useMintHistories = (
	page: number,
	size: number,
	queryParams: Partial<TMintHistoryQueryParams>
) => {
	const { data, isFetching, isFetched } = useQuery({
		refetchInterval: (data) => {
			const rows = data?.state?.data?.rows || [];

			if (isAllStatusSuccess(rows)) {
				return false;
			}

			return 5000;
		},
		queryKey: [
			'mint-histories',
			{
				page: page,
				size: size,
				queryParams: queryParams,
			},
		],
		queryFn: () =>
			getMintHistories(page, size, queryParams),
	});

	const updateList = (mintHistories: TMintHistory[]) => {
		try {
			if (mintHistories.length === 0) return [];

			return mintHistories.reduce(
				(
					acc: TMintHistoryModified[],
					currentItem: TMintHistory
				) => {
					const modifiedItem: TMintHistoryModified = {
						...currentItem,
						project: currentItem.project
							? currentItem.project.name
							: NOT_APPLICABLE,
						mintDate: formatDate(
							currentItem.mint.updatedAt,
							'DD/MM/YYYY'
						),
						nbrOfProductions:
							currentItem &&
							currentItem.productions &&
							Array.isArray(currentItem.productions)
								? formatNumberWithSpaces(
										Number(currentItem.productions.length)
									)
								: 0,
						tCO2eqProduct: convertGCO2EToTCO2E(
							currentItem.quantity.total_aco2
						),
						progress: getMintHistoryPercent(
							currentItem.mint.status,
							currentItem.pin.status,
							currentItem.status
						),
					};
					acc.push(modifiedItem);
					return acc;
				},
				[]
			);
		} catch (error) {
			console.error(
				'An error occurred while processing the list:',
				error
			);
			return [];
		}
	};

	return {
		data:
			data && Array.isArray(data?.rows)
				? updateList(data?.rows)
				: [],
		isLoading: isFetching && !isFetched,
		isFetched,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
